(() => {
  const processedScripts = new WeakSet();
  const scopeObservers = new WeakMap();

  const getPath = (url) => {
    if (!url) return '';
    try {
      return new URL(url, location.origin).pathname;
    } catch {
      return url.split('?')[0];
    }
  };

  const normalizeLang = (value) => (value || '').trim().toLowerCase().split(/[-_]/)[0];
  const withWidth = (url, width) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, location.origin);
      if (width) {
        parsed.searchParams.set('width', String(width));
      } else {
        parsed.searchParams.delete('width');
      }
      return parsed.toString();
    } catch {
      const base = url.split('?')[0];
      return width ? `${base}?width=${width}` : base;
    }
  };

  const parseSrcsetWidths = (srcset) => {
    if (!srcset) return [];
    return srcset
      .split(',')
      .map((part) => {
        const match = part.trim().match(/(\d+)w$/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((w) => w != null);
  };

  const buildSrcset = (src, widths) => {
    if (!src || !widths.length) return '';
    return widths.map((w) => `${withWidth(src, w)} ${w}w`).join(', ');
  };

  const applyToImg = (img, targetSrc) => {
    const srcset = img.getAttribute('srcset') || img.getAttribute('data-srcset');
    const widths = parseSrcsetWidths(srcset);
    const newSrcset = buildSrcset(targetSrc, widths);
    if (newSrcset) {
      img.setAttribute('srcset', newSrcset);
      if (img.hasAttribute('data-srcset')) {
        img.setAttribute('data-srcset', newSrcset);
      }
    }

    const widthAttr = parseInt(img.getAttribute('width'), 10);
    const fallbackWidth = Number.isFinite(widthAttr) ? widthAttr : widths[0];
    const newSrc = withWidth(targetSrc, fallbackWidth);
    if (newSrc) {
      img.setAttribute('src', newSrc);
      if (img.hasAttribute('data-src')) {
        img.setAttribute('data-src', newSrc);
      }
    }
  };

  const applyToSource = (source, targetSrc) => {
    const srcset = source.getAttribute('srcset');
    const widths = parseSrcsetWidths(srcset);
    const newSrcset = buildSrcset(targetSrc, widths);
    if (newSrcset) {
      source.setAttribute('srcset', newSrcset);
    }
  };

  const buildMap = (replacements, locale) => {
    const map = new Map();
    if (!Array.isArray(replacements)) return map;
    replacements.forEach((entry) => {
      if (!entry?.source || !entry?.target) return;
      const entryLang = normalizeLang(entry.language);
      if (entryLang && locale && entryLang !== locale) return;
      const key = getPath(entry.source);
      if (key) map.set(key, entry.target);
    });
    return map;
  };

  const applyReplacements = (node, map) => {
    if (!node || map.size === 0) return;

    const processImg = (img) => {
      const src = img.currentSrc || img.getAttribute('src') || img.getAttribute('data-src') || '';
      const replacement = map.get(getPath(src));
      if (replacement) applyToImg(img, replacement);
    };

    const processSource = (source) => {
      const srcset = source.getAttribute('srcset') || '';
      const firstUrl = srcset.split(',')[0]?.trim().split(' ')[0];
      const replacement = map.get(getPath(firstUrl));
      if (replacement) applyToSource(source, replacement);
    };

    if (node.matches?.('img')) processImg(node);
    else if (node.matches?.('source')) processSource(node);

    node.querySelectorAll?.('img').forEach(processImg);
    node.querySelectorAll?.('source').forEach(processSource);
  };

  const observeScope = (scope, map) => {
    if (scopeObservers.has(scope)) return;
    let scheduled = false;
    const observer = new MutationObserver((mutations) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) {
              applyReplacements(node, map);
            }
          });
        });
      });
    });
    observer.observe(scope, {childList: true, subtree: true});
    scopeObservers.set(scope, observer);
  };

  const applyDataToScope = (scope, data) => {
    const replacements = Array.isArray(data?.replacements) ? data.replacements : [];
    if (replacements.length === 0) return;
    const locale = normalizeLang(document.documentElement.lang);
    const map = buildMap(replacements, locale);
    applyReplacements(scope, map);
    observeScope(scope, map);
  };

  const processScript = (script) => {
    if (!script || processedScripts.has(script)) return;
    processedScripts.add(script);
    const json = script.textContent?.trim();
    if (!json) return;
    let data;
    try {
      data = JSON.parse(json);
    } catch {
      return;
    }
    const scope = script.closest('[data-media-replacements-scope]') || script.parentElement || document;
    applyDataToScope(scope, data);
  };

  const scan = (root) => {
    const scripts = root.querySelectorAll?.('script[data-media-replacements]');
    if (scripts && scripts.length) scripts.forEach(processScript);
  };

  const deferredScopes = new WeakSet();
  const deferredCache = new Map();

  const fetchSectionDocument = (url) => {
    const hydration = window.FoxTheme && window.FoxTheme.hydration;
    if (hydration) return hydration.fetchSection(url, 'media-replacements');

    let sectionUrl;
    try {
      sectionUrl = new URL(url, location.origin);
    } catch {
      return Promise.resolve(null);
    }
    if (sectionUrl.origin !== location.origin) return Promise.resolve(null);
    sectionUrl.searchParams.set('section_id', 'media-replacements');
    return fetch(sectionUrl.toString())
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => (html ? new DOMParser().parseFromString(html, 'text/html') : null))
      .catch(() => null);
  };

  const fetchReplacements = (url) => {
    if (deferredCache.has(url)) return deferredCache.get(url);
    const promise = fetchSectionDocument(url)
      .then((doc) => {
        if (!doc) return null;
        const script = doc.querySelector('script[data-media-replacements]');
        if (!script) return null;
        try {
          return JSON.parse(script.textContent.trim());
        } catch {
          return null;
        }
      })
      .catch(() => null);
    deferredCache.set(url, promise);
    return promise;
  };

  const processDeferredScope = (scope) => {
    if (!scope || deferredScopes.has(scope)) return;
    const url = scope.getAttribute('data-media-replacements-url');
    if (!url) return;
    deferredScopes.add(scope);

    const load = () =>
      fetchReplacements(url).then((data) => {
        if (data) applyDataToScope(scope, data);
      });

    const hydration = window.FoxTheme && window.FoxTheme.hydration;
    if (hydration) {
      hydration.whenVisible(scope, load);
    } else {
      load();
    }
  };

  const scanDeferred = (root) => {
    if (root.matches?.('[data-media-replacements-url]')) processDeferredScope(root);
    root.querySelectorAll?.('[data-media-replacements-url]').forEach(processDeferredScope);
  };

  const scanAll = (root) => {
    scan(root);
    scanDeferred(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scanAll(document));
  } else {
    scanAll(document);
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches?.('script[data-media-replacements]')) {
          processScript(node);
        } else {
          scanAll(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
